import React, { useRef, useState, useEffect } from "react";
import Plot from "react-plotly.js";
import {
  Data,
  Layout as PlotlyLayout,
  Config as PlotlyConfig,
} from "plotly.js";
import { useReactToPrint } from "react-to-print";

import Container from "../../atoms/Container/Container";
import Print from "../Print/Print";
import Button from "../../atoms/Button/Button";
import { ButtonSizes, ButtonTypes } from "../../atoms/Button/Button.types";
import Icon from "../../atoms/Icon/Icon";
import Logo from "../../molecules/Logo/Logo";
import { IconValues } from "../../atoms/Icon/Icon.types";

import {
  PrimarySkillScores,
  StudentInfo,
  SATVSACTReportProps,
  SatActSection,
  OverallData,
  SectionScoreData,
  INeedleProps,
  IPlotlyChartProps,
  IPlotData,
  IPlotLayout,
  IPlotConfig,
  rangeStep,
} from "./SATVSACTReport.types";
import * as SATVSACTConstants from "./SATVSACTReport.constants";
import "./SATVSACTReport.scss";

const PlotlyChart: React.FC<IPlotlyChartProps> = ({
  plotData,
  plotLayout,
  plotConfig,
}) => {
  return <Plot data={plotData} layout={plotLayout} config={plotConfig} />;
};
const calculateRotationAngle = (value: any) => {
  const minValue = -6;
  const maxValue = 6;
  const minAngle = -90; // Degrees
  const maxAngle = 90; // Degrees
  const range = maxValue - minValue;
  const normalizedValue = (value - minValue) / range;
  return minAngle + normalizedValue * (maxAngle - minAngle);
};

const bgColor = "#ecf0f1";
const stepArray: rangeStep[] = [
  { range: [-6, -5.1], color: "#8f53a1" }, // Color-fill step
  { range: [-5.1, -4.9], color: bgColor }, // Gap

  { range: [-4.9, -4.1], color: "rgba(199, 143, 191, 1)" }, // Color-fill step
  { range: [-4.1, -3.9], color: bgColor }, // Gap

  { range: [-3.9, -3.1], color: "rgba(199, 143, 191, .7)" }, // Color-fill step
  { range: [-3.1, -2.9], color: bgColor }, // Gap

  { range: [-2.9, -2.1], color: "rgba(199, 143, 191, .5)" }, // Color-fill step
  { range: [-2.1, -1.9], color: bgColor }, // Gap

  { range: [-1.9, -1.1], color: "rgba(199, 143, 191, .3)" }, // Color-fill step
  { range: [-1.1, -0.9], color: bgColor }, // Gap

  { range: [-0.9, -0.1], color: "rgba(177, 190, 194, .4)" }, // Color-fill step
  { range: [-0.1, 0.1], color: bgColor }, // Gap

  { range: [0.1, 0.9], color: "rgba(177, 190, 194, .4)" }, // Color-fill step
  { range: [0.9, 1.1], color: bgColor }, // Gap

  { range: [1.1, 1.9], color: "rgba(199, 143, 191, .3)" }, // Color-fill step
  { range: [1.9, 2.1], color: bgColor }, // Gap

  { range: [2.1, 2.9], color: "rgba(199, 143, 191, .5)" }, // Color-fill step
  { range: [2.9, 3.1], color: bgColor }, // Gap

  { range: [3.1, 3.9], color: "rgba(199, 143, 191, .7)" }, // Color-fill step
  { range: [3.9, 4.1], color: bgColor }, // Gap

  { range: [4.1, 4.9], color: "rgba(199, 143, 191, 1)" }, // Color-fill step
  { range: [4.9, 5.1], color: bgColor }, // Gap

  { range: [5.1, 6], color: "#8f53a1" }, // Color-fill step
];

const Needle: React.FC<INeedleProps> = ({ rotationAngle }) => {
  const baseWidth = 20;
  const tipWidth = 1;
  const needleLength = 110;

  const baseX1 = -baseWidth / 2;
  const baseX2 = baseWidth / 2;
  const baseY = 0;
  const tipX1 = -tipWidth / 2;
  const tipX2 = tipWidth / 2;
  const tipY = -needleLength;
  return (
    <div
      style={{
        width: "250px",
        height: "200px",
        position: "absolute",
        overflow: "visible",
        zIndex: 3,
        transformOrigin: "bottom center",
      }}
    >
      <svg
        width="250"
        height="250"
        viewBox="-125 -75 250 150"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: "relative", bottom: "-27px" }}
      >
        <polygon
          points={`${baseX1},${baseY} ${baseX2},${baseY} ${tipX2},${tipY} ${tipX1},${tipY}`}
          transform={`rotate(${rotationAngle})`}
          fill="#636f73"
        />
        <circle
          cx="0"
          cy="0"
          r={baseWidth / 2}
          stroke="#636f73"
          strokeWidth="0px"
          fill="#636f73"
        />
      </svg>
    </div>
  );
};

const SATVSACTReport: React.FC<SATVSACTReportProps> = ({ report }) => {
  const student = report.studentInfo;
  const overall = report.sectionScores.overall;
  const primaryScore = report.primarySkillScores;
  const verbal = report.sectionScores.verbal;
  const stem = report.sectionScores.stem;
  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const [overallIndicatorFromCalc, setOverallIndicatorFromCalc] = useState(0);
  const [verbalIndicatorFromCalc, setVerbalIndicatorFromCalc] = useState(0);
  const [stemIndicatorFromCalc, setStemIndicatorFromCalc] = useState(0);

  const [overallRangeText, setOverallRangeText] = useState("");
  const [verbalRangeText, setVerbalRangeText] = useState("");
  const [stemRangeText, setStemRangeText] = useState("");

  const runChartSequence = (indicatorValue: number, chartSection: string) => {
    let timeouts = new Array();
    indicatorValue = indicatorValue > 6 ? 6 : indicatorValue;
    indicatorValue = indicatorValue < -6 ? -6 : indicatorValue;
    function flipSign(currentSign: number) {
      let whichSign = Math.sign(currentSign);
      switch (whichSign) {
        case 1:
          return -Math.abs(currentSign);
        case -1:
          return Math.abs(currentSign);
        default:
          return 0;
      }
    }

    const indicatorValueResult = flipSign(indicatorValue);
    const text = findRangeText(indicatorValueResult);
    const rangeTextSetters: {
      [key: string]: React.Dispatch<React.SetStateAction<string>>;
    } = {
      overall: setOverallRangeText,
      verbal: setVerbalRangeText,
      stem: setStemRangeText,
    };

    rangeTextSetters[chartSection](text);

    const indicatorSetters: {
      [key: string]: React.Dispatch<React.SetStateAction<number>>;
    } = {
      overall: setOverallIndicatorFromCalc,
      verbal: setVerbalIndicatorFromCalc,
      stem: setStemIndicatorFromCalc,
    };

    const increments = 60;
    const totalDuration = 500;
    const incrementValue = indicatorValueResult / increments;
    const intervalTime = totalDuration / increments;

    for (let i = 1; i <= increments; i++) {
      const timeoutId = setTimeout(() => {
        const setIndicator = indicatorSetters[chartSection];
        setIndicator((prevValue) => {
          return i === increments
            ? indicatorValueResult
            : prevValue + incrementValue;
        });
      }, i * intervalTime);
      timeouts.push(timeoutId);
    }

    return () => timeouts.forEach(clearTimeout);
  };

  useEffect(() => {
    let indicatorValue = overall.act.midpoint - overall.sat.equivalentMidpoint;
    runChartSequence(indicatorValue, "overall");
  }, [overall]);

  useEffect(() => {
    let indicatorValue = verbal.act.midpoint - verbal.sat.equivalentMidpoint;
    runChartSequence(indicatorValue, "verbal");
  }, [verbal]);

  useEffect(() => {
    let indicatorValue = stem.act.midpoint - stem.sat.equivalentMidpoint;
    runChartSequence(indicatorValue, "stem");
  }, [stem]);

  const findRangeText = (value: number) => {
    let outputText = "";

    if (value >= -6 && value <= -4) {
      outputText = "Strong ACT";
    }
    if (value > -4 && value <= -2) {
      outputText = "Leans ACT";
    }
    if (value > -2 && value < 2) {
      outputText = "Student Preference";
    }
    if (value >= 2 && value < 4) {
      outputText = "Leans SAT";
    }
    if (value >= 4 && value <= 6) {
      outputText = "Strong SAT";
    }
    return outputText;
  };

  const plotData: Data[] = [
    {
      type: "indicator",
      value: 12,
      domain: { row: 0, column: 0 },
      gauge: {
        bordercolor: bgColor,
        shape: "angular",
        bar: { color: "rgba(0, 0, 0, 0)" },
        axis: {
          visible: false,
          range: [-6, 6],
          tickvals: [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6],
          ticks: "outside",
          tickwidth: 1,
          tickcolor: "black",
        },
        steps: stepArray,
      },
    },
  ];

  const verbalPlotData: Data[] = [...plotData];
  const stemPlotData: Data[] = [...plotData];

  const layout: Partial<PlotlyLayout> = {
    width: 250,
    height: 150,
    margin: { t: 0, b: 0, l: 0, r: 0 },
    paper_bgcolor: bgColor,
    plot_bgcolor: bgColor,
    annotations: [
      {
        x: 0.5,
        y: -0.2,
        xref: "paper",
        yref: "paper",
        showarrow: false,
        arrowhead: 2,
        arrowsize: 1,
        arrowwidth: 2,
        ax: 0,
        ay: -50,
        font: {
          size: 16,
        },
      },
    ],

    template: {
      data: {
        indicator: [
          {
            mode: "gauge",
          },
        ],
      },
    },
  };

  const config: Partial<PlotlyConfig> = {
    displayModeBar: false,
    modeBarButtonsToRemove: [
      "pan2d",
      "select2d",
      "lasso2d",
      "resetScale2d",
      "zoomOut2d",
      "zoomIn2d",
    ],
  };

  return (
    <div className="mp-templates-satvsactreport--main">
      {report && (
        <>
          <Container>
            <header className="satvsact-header-container">
              <div className="satvsact-print-button">
                <Button
                  primary={true}
                  label={<>Print</>}
                  size={ButtonSizes.icon}
                  type={ButtonTypes.neutral}
                  onClick={handlePrint}
                />
              </div>
              <div className="satact-report-report-title">
                <section>
                  <h1>SAT_ACT Report</h1>
                  <StudentHeader student={student} />
                </section>
              </div>
            </header>
            <Overall
              overallData={overall}
              plotData={plotData}
              plotLayout={layout}
              plotConfig={config}
              indicatorVal={overallIndicatorFromCalc}
              rangeText={overallRangeText}
            />
            <PrimaryScore student={student} primaryScoreData={primaryScore} />
            <Verbal
              verbalData={verbal}
              plotData={verbalPlotData}
              plotLayout={layout}
              plotConfig={config}
              indicatorVal={verbalIndicatorFromCalc}
              rangeText={verbalRangeText}
            />
            <Stem
              stemData={stem}
              plotData={stemPlotData}
              plotLayout={layout}
              plotConfig={config}
              indicatorVal={stemIndicatorFromCalc}
              rangeText={stemRangeText}
            />
            <section className="satact-report-disclaimer">
              <small>
                All equivalence scores are based on{" "}
                <a
                  target="_blank"
                  href="https://collegereadiness.collegeboard.org/pdf/guide-2018-act-sat-concordance.pdf"
                >
                  concordance tables published by the ACT and College Board
                </a>
                . Published concordance tables include prediction error between
                tests (i.e. ACT vs. SAT) as well as within a test (sub-scores to
                overall score). As such, sub-scores do not always add up to
                composite scores. Users should rely on the Mindprint test
                recommendation ranges which are designed to account for the
                standard error intervals in the correlations provided by the
                test publishers.
              </small>
            </section>
          </Container>
          <Print ref={componentRef}>
            <div className="mp-templates-satvsactreport--main-print">
              <div className="satact-report-page">
                <header className="satvsact-header-container">
                  <div className="satact-report-report-title">
                    <section>
                      <h1>ACT vs SAT Comparions</h1>
                      <StudentHeader student={student} />
                    </section>
                    <div className="satact-report-print-mp-logo">
                      <Logo responsive={true} />
                    </div>
                  </div>
                </header>
                <Overall
                  overallData={overall}
                  plotData={plotData}
                  plotLayout={layout}
                  plotConfig={config}
                  indicatorVal={overallIndicatorFromCalc}
                  rangeText={overallRangeText}
                />
                <PrimaryScore
                  student={student}
                  primaryScoreData={primaryScore}
                />
                <Verbal
                  verbalData={verbal}
                  plotData={verbalPlotData}
                  plotLayout={layout}
                  plotConfig={config}
                  indicatorVal={verbalIndicatorFromCalc}
                  rangeText={verbalRangeText}
                />
                <Stem
                  stemData={stem}
                  plotData={stemPlotData}
                  plotLayout={layout}
                  plotConfig={config}
                  indicatorVal={stemIndicatorFromCalc}
                  rangeText={stemRangeText}
                />
                <div className="satact-report-disclaimer">
                  All equivalence scores are based on{" "}
                  <a
                    target="_blank"
                    href="https://collegereadiness.collegeboard.org/pdf/guide-2018-act-sat-concordance.pdf"
                  >
                    concordance tables published by the ACT and College Board
                  </a>
                  . Published concordance tables include prediction error
                  between tests (i.e. ACT vs. SAT) as well as within a test
                  (sub-scores to overall score). As such, sub-scores do not
                  always add up to composite scores. Users should rely on the
                  Mindprint test recommendation ranges which are designed to
                  account for the standard error intervals in the correlations
                  provided by the test publishers.
                </div>
              </div>
            </div>
          </Print>
        </>
      )}
    </div>
  );
};

export default SATVSACTReport;

const StudentHeader: React.FC<{
  student: StudentInfo;
}> = ({ student }) => {
  return (
    <div className="satact-student-header">
      <strong>Student:</strong> {`${student?.firstName} ${student?.lastName}`}
      <br />
      <strong>Age:</strong> {student.age}
    </div>
  );
};

const Overall: React.FC<{
  overallData: OverallData;
  plotData: Data[];
  plotLayout: Partial<PlotlyLayout>;
  plotConfig: Partial<PlotlyConfig>;
  indicatorVal: number;
  rangeText: string;
}> = ({
  overallData,
  plotData,
  plotLayout,
  plotConfig,
  indicatorVal,
  rangeText,
}) => {
  const overallRotationAngle = calculateRotationAngle(indicatorVal);

  return (
    <div className="satact-report-section-bg satact-overall-container">
      <header className="satact-overall-header satact-report-logo-header">
        <span className="satact-report-header-logo">
          <Icon value={IconValues.overall} />
        </span>
        <h2 className="satact-report-title">Overall</h2>
      </header>
      <section className="satact-overall-score">
        <div className="satact-report-column act-score">
          <h3 className="satact-report-main-score">{overallData.act.score}</h3>
          <div className="act-compared">
            <div className="satact-report-bold-subheader">
              ACT Midpoint: {overallData.act.midpoint}
            </div>
            Stretch Score:
            {` ${overallData.act.stretchLow} -
            ${overallData.act.stretchHigh}`}
            <br />
            <br />
            <div className="satact-report-bold-subheader">SAT Equivalent*</div>
            Midpoint: {overallData.sat.equivalentMidpoint}
            <br />
            Stretch Score:
            {` ${overallData.sat.stretchLow} - ${overallData.sat.stretchHigh}`}
          </div>
        </div>
        <div
          className="satact-report-column plot-gauge"
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <PlotlyChart
            plotData={plotData}
            plotLayout={plotLayout}
            plotConfig={plotConfig}
          />
          <Needle rotationAngle={overallRotationAngle} />

          <div className="satact-report-range-text">{rangeText}</div>
        </div>
        <div className="satact-report-column sat-score">
          <h3 className="satact-report-main-score">{overallData.sat.score}</h3>
          <div className="sat-compared">
            <div className="satact-report-bold-subheader">
              SAT Midpoint: {overallData.sat.midpoint}
            </div>
            Stretch Score:
            {` ${overallData.sat.stretchLow} -
            ${overallData.sat.stretchHigh}`}
            <br /> <br />
            <div className="satact-report-bold-subheader">ACT Equivalent*</div>
            Midpoint: {overallData.act.equivalentMidpoint}
            <br />
            Stretch Score:
            {` ${overallData.act.equivalentStretchLow} - ${overallData.act.equivalentStretchHigh}`}
          </div>
        </div>
      </section>
    </div>
  );
};

const PrimaryScore: React.FC<{
  primaryScoreData: PrimarySkillScores;
  student: StudentInfo;
}> = ({ primaryScoreData, student }) => {
  function colorCategory(category: string) {
    const categoryCase = category.toLowerCase();
    switch (categoryCase) {
      case "strength":
        return "#0081b8";
      case "high":
        return "#00aeaf";
      case "medium":
        return "#00a550";
      case "low":
        return "#f8c738";
      case "support":
        return "#ea8b35";
      default:
        return "#ccc";
    }
  }
  return (
    <div className="satact-report-section-bg satact-overall-container">
      <header className="satact-overall-header satact-report-columns-wrapper">
        <h2
          className="satact-report-column satact-report-title"
          style={{ textAlign: "center" }}
        >
          ACT
        </h2>
        <div
          className="satact-report-column satact-report-primary"
          style={{ textAlign: "center" }}
        >
          <h3>Primary Score Drivers</h3>
          <span>{`${student?.firstName}'s `}Mindprint Skills</span>
        </div>
        <h2
          className="satact-report-column satact-report-title"
          style={{ textAlign: "center" }}
        >
          SAT
        </h2>
      </header>
      <div className="satact-report-pills">
        <div
          className="satact-report-pill-full-row"
          style={{
            backgroundColor: colorCategory(
              primaryScoreData.verbalReasoningAccuracy.percentileRange.category
            ),
            borderColor: colorCategory(
              primaryScoreData.verbalReasoningAccuracy.percentileRange.category
            ),
          }}
        >
          Verbal Reasoning Accuracy:{" "}
          <em>
            {primaryScoreData.verbalReasoningAccuracy.percentileRange.category}
          </em>
        </div>
        <div className="satact-report-pill-split-row">
          <div
            className="satact-report-pill-left"
            style={{
              backgroundColor: colorCategory(
                primaryScoreData.act[0].percentileRange.category
              ),
              borderColor: colorCategory(
                primaryScoreData.act[0].percentileRange.category
              ),
            }}
          >
            <div className="satact-report-inside-pill">
              {primaryScoreData.act[0].name}
            </div>
            <span>{primaryScoreData.act[0].percentileRange.category}</span>
          </div>
          <div
            className="satact-report-pill-right"
            style={{
              backgroundColor: colorCategory(
                primaryScoreData.sat[0].percentileRange.category
              ),
              borderColor: colorCategory(
                primaryScoreData.sat[0].percentileRange.category
              ),
            }}
          >
            <div className="satact-report-inside-pill">
              {primaryScoreData.sat[0].name}
            </div>
            <span>{primaryScoreData.sat[0].percentileRange.category}</span>
          </div>
        </div>
        <div className="satact-report-pill-split-row">
          <div
            className="satact-report-pill-left"
            style={{
              backgroundColor: colorCategory(
                primaryScoreData.act[1].percentileRange.category
              ),
              borderColor: colorCategory(
                primaryScoreData.act[1].percentileRange.category
              ),
            }}
          >
            <div className="satact-report-inside-pill">
              {primaryScoreData.act[1].name}
            </div>
            <span>{primaryScoreData.act[1].percentileRange.category}</span>
          </div>
          <div
            className="satact-report-pill-right"
            style={{
              backgroundColor: colorCategory(
                primaryScoreData.sat[1].percentileRange.category
              ),
              borderColor: colorCategory(
                primaryScoreData.sat[1].percentileRange.category
              ),
            }}
          >
            <div className="satact-report-inside-pill">
              {primaryScoreData.sat[1].name}
            </div>
            <span>{primaryScoreData.sat[1].percentileRange.category}</span>
          </div>
        </div>
        <div className="satact-report-pill-keys">
          <div className="satact-report-pill-box">
            <div className="satact-report-support-bg satact-report-key">
              Support
            </div>
            1% - 16% of peers
          </div>
          <div className="satact-report-pill-box">
            <div className="satact-report-low-bg satact-report-key">Low</div>17%
            ‐ 31% of peers
          </div>
          <div className="satact-report-pill-box">
            <div className="satact-report-medium-bg satact-report-key">
              Medium
            </div>
            32% ‐ 69% of peers
          </div>
          <div className="satact-report-pill-box">
            <div className="satact-report-high-bg satact-report-key">High</div>
            70% ‐ 84% of peers
          </div>
          <div className="satact-report-pill-box">
            <div className="satact-report-strength-bg satact-report-key">
              Strength
            </div>
            top 16% of peers
          </div>
        </div>
      </div>
    </div>
  );
};

const Verbal: React.FC<{
  verbalData: SatActSection;
  plotData: Data[];
  plotLayout: Partial<PlotlyLayout>;
  plotConfig: Partial<PlotlyConfig>;
  indicatorVal: number;
  rangeText: string;
}> = ({
  verbalData,
  plotData,
  plotLayout,
  plotConfig,
  indicatorVal,
  rangeText,
}) => {
  const verbalRotationAngle = calculateRotationAngle(indicatorVal);

  return (
    <div className="satact-report-section-bg satact-overall-container">
      <header className="satact-overall-header satact-report-logo-header">
        <span className="satact-report-header-logo">
          <Icon value={IconValues.booksColored} />
        </span>

        <h2 className="satact-report-title">Verbal</h2>
      </header>
      <section className="satact-overall-score">
        <div className="act-score satact-report-column">
          <h3 className="satact-report-main-score">{verbalData.act.score}</h3>
          <div className="act-compared">
            <div className="bsatact-report-old-subheader">
              ACT Midpoint: {verbalData.act.midpoint}
            </div>
            <div>English & Reading</div>
            <br />
            <div className="satact-report-bold-subheader">
              SAT Equivalent* <br />
            </div>
            Midpoint: {verbalData.sat.equivalentMidpoint}
          </div>
        </div>
        <div
          className="satact-report-column plot-gauge"
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <PlotlyChart
            plotData={plotData}
            plotLayout={plotLayout}
            plotConfig={plotConfig}
          />
          <Needle rotationAngle={verbalRotationAngle} />
          <div className="satact-report-range-text">{rangeText}</div>
        </div>
        <div className="satact-report-column sat-score">
          <h3 className="satact-report-main-score">{verbalData.sat.score}</h3>
          <div className="sat-compared">
            <div className="satact-report-bold-subheader">
              SAT Midpoint: {verbalData.sat.midpoint}
            </div>
            <div>Reading & Writing</div>
            <br />
            <div className="satact-report-bold-subheader">ACT Equivalent*</div>
            Midpoint: {verbalData.act.equivalentMidpoint}
          </div>
        </div>
      </section>
    </div>
  );
};

const Stem: React.FC<{
  stemData: SatActSection;
  plotData: Data[];
  plotLayout: Partial<PlotlyLayout>;
  plotConfig: Partial<PlotlyConfig>;
  indicatorVal: number;
  rangeText: string;
}> = ({
  stemData,
  plotData,
  plotLayout,
  plotConfig,
  indicatorVal,
  rangeText,
}) => {
  const stemRotationAngle = calculateRotationAngle(indicatorVal);

  return (
    <div className="satact-report-section-bg satact-overall-container">
      <header className="satact-overall-header satact-report-logo-header">
        <span className="satact-report-header-logo">
          <Icon value={IconValues.stem} />
        </span>
        <h2 className="satact-report-title">STEM</h2>
      </header>
      <section className="satact-overall-score">
        <div className="act-score satact-report-column">
          <h3 className="satact-report-main-score">{stemData.act.score}</h3>
          <div className="act-compared">
            <div className="satact-report-bold-subheader">
              ACT Midpoint: {stemData.act.midpoint}
            </div>
            <div>Math & Science</div>
            <br />
            <div className="satact-report-bold-subheader">SAT Equivalent*</div>
            Midpoint: {stemData.sat.equivalentMidpoint}
          </div>
        </div>
        <div
          className="satact-report-column plot-gauge"
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          <PlotlyChart
            plotData={plotData}
            plotLayout={plotLayout}
            plotConfig={plotConfig}
          />
          <Needle rotationAngle={stemRotationAngle} />
          <div className="satact-report-range-text">{rangeText}</div>
        </div>
        <div className="satact-report-column sat-score">
          <h3 className="satact-report-main-score">{stemData.sat.score}</h3>
          <div className="sat-compared">
            <div className="satact-report-bold-subheader">
              SAT Midpoint: {stemData.sat.midpoint}{" "}
            </div>
            <div>Math</div>
            <br />
            <div className="satact-report-bold-subheader">ACT Equivalent*</div>
            Midpoint: {stemData.act.equivalentMidpoint}
          </div>
        </div>
      </section>
    </div>
  );
};
